/**
 * AdSlot — Fetches and renders a banner creative for a given slot key.
 *
 * Usage: <AdSlot slot="homepage_hero_below" />
 *
 * Behavior:
 *   - Calls /api/ads/slot/{slot} on mount (server selects creative via weighted random + window).
 *   - If no creative → renders nothing (collapses gracefully).
 *   - On click: fires /api/ads/click/{ad_id} BEFORE navigation.
 *   - Mobile-specific image_url override supported.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { resolveImageUrl } from "@/lib/api";

export default function AdSlot({ slot, className = "" }) {
    const [creative, setCreative] = useState(null);

    useEffect(() => {
        let cancelled = false;
        api.get(`/ads/slot/${slot}`).then((r) => {
            if (!cancelled) setCreative(r.data?.creative || null);
        }).catch(() => { /* silent */ });
        return () => { cancelled = true; };
    }, [slot]);

    if (!creative) return null;

    const handleClick = (e) => {
        // Fire-and-forget tracking; never block navigation
        api.post(`/ads/click/${creative.id}`).catch(() => { /* silent */ });
        // For external links we let the browser handle; for internal Link the navigation already triggered
    };

    const isInternal = creative.cta_url && (creative.cta_url.startsWith("/") || creative.cta_url.startsWith("#"));
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    const imgUrl = isMobile && creative.mobile_image_url ? creative.mobile_image_url : creative.image_url;

    const Inner = (
        <div data-testid={`ad-slot-${slot}`}
            className={`relative overflow-hidden rounded-2xl cursor-pointer group ${className}`}
            style={creative.bg_color && !imgUrl ? { background: creative.bg_color } : undefined}>
            {imgUrl && (
                <img src={resolveImageUrl(imgUrl)} alt={creative.title || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            )}
            {(creative.title || creative.subtitle || creative.cta_label) && (
                <div className={`${imgUrl ? "absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/40 to-transparent" : ""}`}>
                    <div className={`relative p-5 md:p-8 ${imgUrl ? "" : "text-center"}`}>
                        {creative.title && <div className={`font-display font-bold text-xl md:text-2xl ${imgUrl ? "text-white" : "text-white"}`}>{creative.title}</div>}
                        {creative.subtitle && <div className={`mt-1 text-sm md:text-base ${imgUrl ? "text-white/85" : "text-white/85"}`}>{creative.subtitle}</div>}
                        {creative.cta_label && (
                            <span className="inline-block mt-3 px-4 py-1.5 rounded-full bg-white text-slate-900 text-xs font-bold group-hover:bg-amber-300 transition-colors">{creative.cta_label}</span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    if (!creative.cta_url) return Inner;
    if (isInternal) {
        return <Link to={creative.cta_url} onClick={handleClick} className="block">{Inner}</Link>;
    }
    return <a href={creative.cta_url} target="_blank" rel="noreferrer noopener" onClick={handleClick} className="block">{Inner}</a>;
}
